import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronRight, Lightbulb, Gift, Sparkles, Check, CreditCard } from 'lucide-react';
import StaticPageLayout from '../../../components/StaticPageLayout';
import { useThemeStore } from '../../../store/theme';

const tableOfContents = [
  { id: 'overview', title: 'Overview' },
  { id: 'free-credits', title: 'Free Credits on Signup' },
  { id: 'free-plan', title: 'Free Plan Features' },
  { id: 'demo', title: 'Request a Demo' },
  { id: 'upgrade', title: 'Upgrading Your Plan' },
  { id: 'faq', title: 'Frequently Asked Questions' },
];

const relatedArticles = [
  { title: 'Plans and pricing', href: '/doc/article/pricing-and-plan' },
  { title: 'What is SweetPilot?', href: '/doc/article/what-is-sweetpilot' },
  { title: 'How to get started with SweetPilot', href: '/doc/article/getting-started' },
  { title: 'Login to SweetPilot', href: '/doc/article/login-to-sweetpilot' },
];

const freeFeatures = [
  'Access to all AI employees',
  '100 free credits to get started',
  'Brain AI knowledge base',
  'Basic integrations',
  'Chat history',
  'Power-ups and instant tasks',
];

interface TipBoxProps {
  children: React.ReactNode;
  isDark: boolean;
}

function TipBox({ children, isDark }: TipBoxProps) {
  return (
    <div className={`flex gap-3 p-4 rounded-xl my-6 ${
      isDark ? 'bg-purple-500/10 border border-purple-500/20' : 'bg-purple-50 border border-purple-100'
    }`}>
      <Lightbulb className={`w-5 h-5 shrink-0 mt-0.5 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
      <div className={`text-sm ${isDark ? 'text-purple-200' : 'text-purple-800'}`}>
        {children}
      </div>
    </div>
  );
}

export default function DemoFreeTrial() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const [activeSection, setActiveSection] = useState('');
  const [isAtTop, setIsAtTop] = useState(true);

  // SEO Schema markup
  const schemaMarkup = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'Can I Get a Demo or Free Trial? - SweetPilot Free Credits',
    description: 'Learn about SweetPilot free trial options. Get 100 free credits on signup, explore the free plan features, and request a personalized demo.',
    author: { '@type': 'Organization', name: 'SweetPilot' },
    publisher: { '@type': 'Organization', name: 'SweetPilot', logo: { '@type': 'ImageObject', url: 'https://sweetpilot.com/logo.png' } },
    datePublished: '2024-01-01',
    dateModified: new Date().toISOString().split('T')[0],
    mainEntityOfPage: { '@type': 'WebPage', '@id': 'https://sweetpilot.com/doc/article/demo-free-trial' }
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = tableOfContents.map(item => document.getElementById(item.id));
      const scrollPosition = window.scrollY + 100;

      const firstSection = sections[0];
      if (firstSection && scrollPosition < firstSection.offsetTop) {
        setIsAtTop(true);
        setActiveSection('');
        return;
      }

      setIsAtTop(false);

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(tableOfContents[i].id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
    }
  };

  return (
    <>
      <Helmet>
        <title>Can I Get a Demo or Free Trial? - 100 Free Credits | SweetPilot Help Center</title>
        <meta name="description" content="Get 100 free credits when you sign up for SweetPilot. No credit card required. Explore the free plan, request a demo, and discover all features before upgrading." />
        <meta name="keywords" content="SweetPilot free trial, SweetPilot demo, free credits, AI platform trial, SweetPilot free plan, business automation trial, AI employee demo" />
        <meta property="og:title" content="SweetPilot Free Trial - Get 100 Free Credits" />
        <meta property="og:description" content="Start with 100 free credits. No credit card required. Request a personalized demo." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://sweetpilot.com/doc/article/demo-free-trial" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="SweetPilot Free Trial & Demo" />
        <meta name="twitter:description" content="Get 100 free credits and explore SweetPilot risk-free." />
        <link rel="canonical" href="https://sweetpilot.com/doc/article/demo-free-trial" />
        <script type="application/ld+json">{JSON.stringify(schemaMarkup)}</script>
      </Helmet>

      <StaticPageLayout
        title=""
        subtitle=""
        showBackButton={false}
      >
        <div className="flex gap-16 lg:gap-20">
          {/* Main Content */}
        <div className="flex-1 max-w-2xl">
          {/* Breadcrumbs */}
          <nav className="mb-8">
            <ol className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
              <li>
                <Link
                  to="/docs"
                  className={`transition-colors ${isDark ? 'hover:text-purple-400' : 'hover:text-purple-600'}`}
                >
                  All Collections
                </Link>
              </li>
              <li>
                <ChevronRight className="w-4 h-4" />
              </li>
              <li>
                <Link
                  to="/doc/collections/getting-started"
                  className={`transition-colors ${isDark ? 'hover:text-purple-400' : 'hover:text-purple-600'}`}
                >
                  Getting started
                </Link>
              </li>
              <li>
                <ChevronRight className="w-4 h-4" />
              </li>
              <li className={isDark ? 'text-white' : 'text-slate-900'}>
                Can I get a demo or a free trial?
              </li>
            </ol>
          </nav>

          {/* Article Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-10"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                isDark ? 'bg-purple-500/20' : 'bg-purple-100'
              }`}>
                <Gift className={`w-6 h-6 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
              </div>
              <h1 className={`text-4xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Can I get a demo or a free trial?
              </h1>
            </div>
            <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
              Learn about SweetPilot's free credits, trial options, and how to request a personalized demo.
            </p>
          </motion.div>

          {/* Article Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className={`prose max-w-none ${isDark ? 'prose-invert' : ''}`}
          >
            {/* Overview */}
            <h2 id="overview" className={`text-2xl font-bold mt-12 mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Overview
            </h2>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <strong>Yes!</strong> SweetPilot offers multiple ways to try our platform before committing to a paid plan:
            </p>
            <ul className={`list-disc pl-6 mb-6 space-y-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <li><strong>Free credits</strong> - Get 100 free credits when you sign up</li>
              <li><strong>Free plan</strong> - Access core features at no cost</li>
              <li><strong>Personalized demo</strong> - Request a walkthrough from our team</li>
            </ul>
            <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              We believe you should experience SweetPilot's power before making any financial commitment. That's why we make it easy to get started for free.
            </p>

            {/* Free Credits on Signup */}
            <h2 id="free-credits" className={`text-2xl font-bold mt-12 mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Free Credits on Signup
            </h2>

            <div className={`p-6 rounded-2xl border mb-6 ${
              isDark ? 'bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border-white/10' : 'bg-gradient-to-br from-purple-50 to-cyan-50 border-slate-200'
            }`}>
              <div className="flex items-center gap-4 mb-4">
                <div className={`w-16 h-16 rounded-xl flex items-center justify-center ${
                  isDark ? 'bg-purple-500/20' : 'bg-purple-100'
                }`}>
                  <Sparkles className={`w-8 h-8 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                </div>
                <div>
                  <h3 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>100 Free Credits</h3>
                  <p className={`${isDark ? 'text-gray-400' : 'text-slate-600'}`}>Automatically added to your account</p>
                </div>
              </div>
              <p className={`${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                When you create a new SweetPilot account, you'll automatically receive <strong>100 free credits</strong> to explore the platform. No credit card required!
              </p>
            </div>

            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              With your free credits, you can:
            </p>
            <ul className={`list-disc pl-6 mb-6 space-y-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <li>Chat with any AI employee</li>
              <li>Generate content, analyze data, and more</li>
              <li>Try power-ups and instant tasks</li>
              <li>Set up Brain AI with your business information</li>
              <li>Connect integrations</li>
            </ul>

            <TipBox isDark={isDark}>
              <strong>Tip:</strong> Your free credits don't expire! Take your time exploring SweetPilot at your own pace.
            </TipBox>

            {/* Free Plan Features */}
            <h2 id="free-plan" className={`text-2xl font-bold mt-12 mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Free Plan Features
            </h2>
            <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              Even after using your initial credits, our free plan gives you access to core SweetPilot features:
            </p>

            <div className={`p-6 rounded-2xl border mb-6 ${
              isDark ? 'bg-dark-800/50 border-white/10' : 'bg-white border-slate-200'
            }`}>
              <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                What's included in Free:
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {freeFeatures.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Check className={`w-5 h-5 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                    <span className={`${isDark ? 'text-gray-300' : 'text-slate-700'}`}>{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              The free plan is perfect for individuals who want to explore SweetPilot's capabilities or use it for occasional tasks. For heavier usage and advanced features, consider upgrading to a paid plan.
            </p>

            {/* Request a Demo */}
            <h2 id="demo" className={`text-2xl font-bold mt-12 mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Request a Demo
            </h2>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              Want a personalized walkthrough of SweetPilot? Our team is happy to show you how the platform can help your specific business needs.
            </p>
            <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              During a demo, we can:
            </p>
            <ul className={`list-disc pl-6 mb-6 space-y-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <li>Walk you through all platform features</li>
              <li>Show you relevant use cases for your industry</li>
              <li>Answer any questions you have</li>
              <li>Help you understand which plan is right for you</li>
              <li>Provide guidance on setting up your account</li>
            </ul>

            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Link
                to="/contact"
                className={`inline-flex items-center justify-center px-6 py-3 rounded-xl font-medium transition-all ${
                  isDark
                    ? 'bg-gradient-to-r from-purple-500 to-cyan-500 text-white hover:shadow-lg hover:shadow-purple-500/25'
                    : 'bg-gradient-to-r from-purple-500 to-cyan-500 text-white hover:shadow-lg hover:shadow-purple-500/25'
                }`}
              >
                Request a Demo
              </Link>
              <Link
                to="/login"
                className={`inline-flex items-center justify-center px-6 py-3 rounded-xl font-medium transition-colors ${
                  isDark
                    ? 'bg-white/10 text-white hover:bg-white/20'
                    : 'bg-slate-100 text-slate-900 hover:bg-slate-200'
                }`}
              >
                Get Started
              </Link>
            </div>

            <TipBox isDark={isDark}>
              <strong>For Enterprise:</strong> If you're looking for a solution for your team or organization, we offer custom enterprise demos with dedicated onboarding support.{' '}
              <Link
                to="/contact"
                className={`font-medium underline ${isDark ? 'text-purple-300 hover:text-purple-200' : 'text-purple-700 hover:text-purple-800'}`}
              >
                Contact our sales team
              </Link>.
            </TipBox>

            {/* Upgrading Your Plan */}
            <h2 id="upgrade" className={`text-2xl font-bold mt-12 mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Upgrading Your Plan
            </h2>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              Ready for more? Upgrading is simple and can be done anytime from your dashboard.
            </p>

            <div className={`p-6 rounded-2xl border mb-6 ${
              isDark ? 'bg-dark-800/50 border-white/10' : 'bg-white border-slate-200'
            }`}>
              <div className="flex items-center gap-4 mb-4">
                <CreditCard className={`w-8 h-8 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  How to Upgrade
                </h3>
              </div>
              <ol className={`list-decimal pl-6 space-y-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                <li>Log in to your SweetPilot account</li>
                <li>Go to <strong>Billing</strong> in the sidebar</li>
                <li>Choose your preferred plan</li>
                <li>Enter your payment details</li>
                <li>Start enjoying premium features immediately!</li>
              </ol>
            </div>

            <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              For detailed information about our plans and pricing, visit our{' '}
              <Link
                to="/pricing"
                className={`font-medium underline ${isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'}`}
              >
                pricing page
              </Link>.
            </p>

            {/* FAQ */}
            <h2 id="faq" className={`text-2xl font-bold mt-12 mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Frequently Asked Questions
            </h2>

            <div className="space-y-6 mb-8">
              <div>
                <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Do I need a credit card to sign up?
                </h3>
                <p className={`${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                  No! You can create an account and start using SweetPilot with your free credits without entering any payment information.
                </p>
              </div>

              <div>
                <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Do the free credits expire?
                </h3>
                <p className={`${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                  No, your free credits don't have an expiration date. Use them whenever you're ready!
                </p>
              </div>

              <div>
                <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Can I get more free credits?
                </h3>
                <p className={`${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                  Yes! You can earn additional credits through our referral program. Share your unique referral code with friends, and both you and your friend will receive bonus credits when they sign up.
                </p>
              </div>

              <div>
                <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  What happens when I run out of credits?
                </h3>
                <p className={`${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                  When your credits run out, you can either purchase more credits or upgrade to a subscription plan that includes monthly credits. You won't lose access to your account or data.
                </p>
              </div>

              <div>
                <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Is there a money-back guarantee?
                </h3>
                <p className={`${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                  Yes, we offer a satisfaction guarantee on our paid plans. If you're not happy with SweetPilot, contact us within the first 7 days for a full refund. See our{' '}
                  <Link
                    to="/refund-policy"
                    className={`font-medium underline ${isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'}`}
                  >
                    refund policy
                  </Link>{' '}
                  for details.
                </p>
              </div>

              <div>
                <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  How long does a demo take?
                </h3>
                <p className={`${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                  A typical demo takes about 30 minutes, but we can adjust based on your needs and questions. We're flexible!
                </p>
              </div>
            </div>

            {/* CTA Section */}
            <div className={`p-8 rounded-2xl text-center ${
              isDark ? 'bg-gradient-to-br from-purple-500/20 to-cyan-500/20 border border-white/10' : 'bg-gradient-to-br from-purple-50 to-cyan-50 border border-slate-200'
            }`}>
              <h3 className={`text-2xl font-bold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Ready to get started?
              </h3>
              <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
                Create your free account today and get 100 credits to explore SweetPilot.
              </p>
              <Link
                to="/login"
                className="inline-flex items-center justify-center px-8 py-3 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-xl font-medium text-white hover:shadow-lg hover:shadow-purple-500/25 transition-all"
              >
                Get Started
              </Link>
            </div>

            {/* Divider */}
            <hr className={`my-12 ${isDark ? 'border-white/10' : 'border-slate-200'}`} />

            {/* Related Articles */}
            <h2 className={`text-2xl font-bold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Related Articles
            </h2>
            <div className={`rounded-2xl border overflow-hidden ${
              isDark ? 'bg-dark-800/50 border-white/10' : 'bg-white border-slate-200'
            }`}>
              {relatedArticles.map((article, index) => (
                <Link
                  key={index}
                  to={article.href}
                  className={`flex items-center justify-between p-4 transition-colors ${
                    index !== relatedArticles.length - 1 ? (isDark ? 'border-b border-white/5' : 'border-b border-slate-100') : ''
                  } ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}
                >
                  <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    {article.title}
                  </span>
                  <ChevronRight className={`w-5 h-5 ${isDark ? 'text-gray-500' : 'text-slate-400'}`} />
                </Link>
              ))}
            </div>

            {/* Feedback Section */}
            <div className={`mt-12 p-8 rounded-2xl text-center ${
              isDark ? 'bg-dark-800/50 border border-white/10' : 'bg-slate-50 border border-slate-200'
            }`}>
              <p className={`text-lg font-medium mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Did this answer your question?
              </p>
              <div className="flex justify-center gap-4">
                <button className="text-3xl hover:scale-110 transition-transform" title="No">😞</button>
                <button className="text-3xl hover:scale-110 transition-transform" title="Somewhat">😐</button>
                <button className="text-3xl hover:scale-110 transition-transform" title="Yes">😃</button>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Right Sidebar - Table of Contents */}
        <aside className="hidden lg:block w-56 shrink-0 ml-auto">
          <div className="sticky top-24 pl-6 border-l border-slate-200 dark:border-white/10">
            {/* Sidebar Title */}
            <h3 className={`text-base mb-4 ${
              isAtTop
                ? isDark ? 'text-white font-semibold' : 'text-slate-900 font-semibold'
                : isDark ? 'text-gray-400' : 'text-slate-600'
            }`}>
              Demo & Free Trial
            </h3>
            <nav className="space-y-2">
              {tableOfContents.map((item, index) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`block w-full text-left py-1.5 text-sm transition-colors ${
                    activeSection === item.id
                      ? isDark
                        ? 'text-white font-medium'
                        : 'text-slate-900 font-medium'
                      : isAtTop && index === 0
                        ? isDark
                          ? 'text-white font-medium'
                          : 'text-slate-900 font-medium'
                        : isDark
                          ? 'text-gray-500 hover:text-gray-300'
                          : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {item.title}
                </button>
              ))}
            </nav>
          </div>
        </aside>
      </div>

      {/* Footer */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="text-center mt-16"
      >
        <div className={`w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center ${
          isDark ? 'bg-purple-500/20' : 'bg-purple-100'
        }`}>
          <span className="text-2xl">🎁</span>
        </div>
        <a
          href="mailto:help@sweetpilot.com"
          className={`text-sm transition-colors ${
            isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'
          }`}
        >
          help@sweetpilot.com
        </a>
      </motion.div>
      </StaticPageLayout>
    </>
  );
}
